import React, { useMemo } from 'react';
import { HStack, Text } from '@chakra-ui/react';
import {
  SubscriptionDiscountInfo,
  SubscriptionDiscountItem,
} from 'types/stripe';

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/**
 * Applies each discount in order: percent off (on the running balance), then fixed amount off.
 * Matches common Stripe-style stacking for display purposes.
 */
export function computeDiscountedPriceCents(
  baseCents: number,
  discounts: SubscriptionDiscountItem[],
  planCurrency: string,
): number {
  let remaining = baseCents;
  const upperPlan = planCurrency.toUpperCase();

  for (const d of discounts) {
    if (d.percentOff != null && d.percentOff > 0) {
      remaining = Math.round(remaining * (1 - d.percentOff / 100));
    }
    if (d.amountOff != null && d.amountOff > 0) {
      const dc = d.currency?.toUpperCase();
      if (!dc || dc === upperPlan) {
        remaining = Math.max(0, remaining - d.amountOff);
      }
    }
  }

  return remaining;
}

interface SubscriptionPriceWithDiscountProps {
  priceCents: number;
  currency: string;
  interval: 'month' | 'year';
  discount?: SubscriptionDiscountInfo | null;
}

export const SubscriptionPriceWithDiscount: React.FC<
  SubscriptionPriceWithDiscountProps
> = ({ priceCents, currency, interval, discount }) => {
  const { discountedCents, hasActiveDiscount } = useMemo(() => {
    if (
      !discount?.applied ||
      !discount.discounts?.length ||
      priceCents <= 0
    ) {
      return { discountedCents: priceCents, hasActiveDiscount: false };
    }

    const next = computeDiscountedPriceCents(
      priceCents,
      discount.discounts,
      currency,
    );
    const lowered = next < priceCents;
    return {
      discountedCents: next,
      hasActiveDiscount: lowered,
    };
  }, [discount, priceCents, currency]);

  const intervalSuffix = interval === 'year' ? '/year' : '/month';

  return (
    <HStack spacing={3} align="baseline" justify="flex-end">
      {hasActiveDiscount ? (
        <Text
          fontSize="md"
          color="gray.500"
          textDecoration="line-through"
          fontWeight="medium"
        >
          {formatMoney(priceCents, currency)}
        </Text>
      ) : null}
      <Text fontSize="lg" fontWeight="semibold" color="brand.500">
        {formatMoney(discountedCents, currency)}
        <Text as="span" fontSize="12px" fontWeight="normal">
          {intervalSuffix}
        </Text>
      </Text>
    </HStack>
  );
};
